import { exhaustiveCheck, isTruthy } from '@ardoq/common-helpers';
import { Column, LinkNameCell } from '@ardoq/table';
import {
  APISurveyResponseLog,
  ArdoqId,
  CreatedResponse,
  DeletedResponse,
  UpdatedResponse,
  UpdateType,
} from '@ardoq/api-types';
import { AnimatedChevron } from '@ardoq/icons';
import { SmallGhostButton } from '@ardoq/button';
import { formatDateTimeShort } from '@ardoq/date-time';
import { trackEvent } from 'tracking/tracking';
import { TruncatedTextPopover } from '@ardoq/popovers';
import type { Locale } from '@ardoq/locale';
import { t, type I18nLocale } from '@ardoq/i18n';
import { FlexBox } from '@ardoq/layout';
import { toLinkProps } from 'router/declarative/toLinkProps';

export const isUpdatedResponse = (
  response: APISurveyResponseLog
): response is UpdatedResponse =>
  response && (response as UpdatedResponse).previousVal !== undefined;

export const isCreatedResponse = (
  response: APISurveyResponseLog
): response is CreatedResponse =>
  response && (response as CreatedResponse).created !== undefined;

export const isDeletedResponse = (
  response: APISurveyResponseLog
): response is DeletedResponse =>
  response && (response as DeletedResponse).deleted !== undefined;

export type ResponseLogRowData = {
  name: {
    displayName: string;
    componentId: ArdoqId | null;
    onClick: VoidFunction;
  };
  whatChanged: string;
  whoChanged: string;
  whenChanged: string;
  expander: {
    isExpandable: boolean;
    isExpanded: boolean;
    onToggle: VoidFunction;
  } | null;
};

const NameCell = ({
  displayName,
  componentId,
  onClick,
}: ResponseLogRowData['name']) => {
  if (componentId) {
    const { href, onClick: navigateToComponent } = toLinkProps({
      route: '/component-overview/:componentId',
      params: { componentId },
    });
    return (
      <LinkNameCell
        displayName={displayName}
        href={href}
        onClick={() => {
          onClick();
          navigateToComponent?.();
        }}
      />
    );
  }
  return <TruncatedTextPopover>{displayName}</TruncatedTextPopover>;
};

const ExpanderCell = ({
  isExpandable,
  isExpanded,
  onToggle,
}: NonNullable<ResponseLogRowData['expander']>) => {
  if (!isExpandable) return null;
  return (
    <FlexBox justify="center" align="center" height="full">
      <SmallGhostButton onClick={onToggle}>
        <AnimatedChevron $isUp={isExpanded} />
      </SmallGhostButton>
    </FlexBox>
  );
};

const getWhoChangedTitle = (
  updateType: UpdateType,
  i18nLocale: I18nLocale
): string => {
  switch (updateType) {
    case UpdateType.UPDATED:
      return t(
        i18nLocale,
        'surveyAdmin:responseLog.changedByColumn',
        'Changed by'
      );
    case UpdateType.CREATED:
      return t(
        i18nLocale,
        'surveyAdmin:responseLog.createdByColumn',
        'Created by'
      );
    case UpdateType.DELETED:
      return t(
        i18nLocale,
        'surveyAdmin:responseLog.deletedByColumn',
        'Deleted by'
      );
    default:
      return exhaustiveCheck(updateType);
  }
};

export const getColumns = (
  updateType: UpdateType,
  i18nLocale: I18nLocale
): Column<ResponseLogRowData>[] => {
  const nameColumn: Column<ResponseLogRowData> = {
    id: 'name',
    title: t(i18nLocale, 'surveyAdmin:responseLog.nameColumn', 'Name'),
    minWidth: updateType === UpdateType.UPDATED ? 222 : 322,
    flex: 3,
    cellRenderer: NameCell,
    valueGetter: ({ displayName }) => displayName,
  };

  const whatChangedColumn: Column<ResponseLogRowData> | undefined =
    updateType === UpdateType.UPDATED
      ? {
          id: 'whatChanged',
          title: t(
            i18nLocale,
            'surveyAdmin:responseLog.whatChangedColumn',
            'What changed'
          ),
          minWidth: 196,
          flex: 2,
        }
      : undefined;

  const whoChangedColumn: Column<ResponseLogRowData> = {
    id: 'whoChanged',
    title: getWhoChangedTitle(updateType, i18nLocale),
    minWidth: updateType === UpdateType.UPDATED ? 216 : 260,
    flex: 2,
  };

  const whenChangedColumn: Column<ResponseLogRowData> = {
    id: 'whenChanged',
    title: t(i18nLocale, 'surveyAdmin:responseLog.whenColumn', 'When'),
    minWidth: 150,
    flex: 1,
  };

  const expanderColumn: Column<ResponseLogRowData> = {
    id: 'expander',
    title: '',
    width: 60,
    resizable: false,
    suppressSorting: true,
    suppressHeaderContextMenu: true,
    cellRenderer: ExpanderCell,
    valueGetter: () => '',
  };

  return [
    nameColumn,
    whatChangedColumn,
    whoChangedColumn,
    whenChangedColumn,
    expanderColumn,
  ].filter(isTruthy);
};

export const getRowName = (response: APISurveyResponseLog): string => {
  if (isUpdatedResponse(response)) return response.component.name;
  return response.name;
};

const getRowUrl = (response: APISurveyResponseLog): string | null => {
  if (isUpdatedResponse(response)) return response.component.url ?? null;
  if (isCreatedResponse(response)) return response.url ?? null;
  return null;
};

export const getComponentIdFromResponse = (
  response: APISurveyResponseLog
): ArdoqId | null => {
  const componentIdMatch = getRowUrl(response)?.match(
    /(?:^|\/)component\/([^/?#]+)/
  );
  return componentIdMatch?.[1] ?? null;
};

export const getRowWhen = (response: APISurveyResponseLog): string => {
  if (isUpdatedResponse(response)) return response.lastUpdated;
  if (isCreatedResponse(response)) return response.created;
  if (isDeletedResponse(response)) return response.deleted;
  return '';
};

type ToResponseRowDataArgs = {
  response: APISurveyResponseLog;
  isExpandable: boolean;
  isExpanded: boolean;
  onToggle: VoidFunction;
  onComponentClick: VoidFunction;
  locale: Locale;
};

export const toResponseRowData = ({
  response,
  isExpandable,
  isExpanded,
  onToggle,
  onComponentClick,
  locale,
}: ToResponseRowDataArgs): ResponseLogRowData => ({
  name: {
    displayName: getRowName(response),
    componentId: getComponentIdFromResponse(response),
    onClick: onComponentClick,
  },
  whatChanged: isUpdatedResponse(response) ? response.name : '',
  whoChanged: response.lastModifiedByName ?? '',
  whenChanged: formatDateTimeShort(getRowWhen(response) || null, locale),
  expander: isExpandable
    ? {
        isExpandable: true,
        isExpanded,
        onToggle: () => {
          onToggle();
          trackEvent(
            `Survey response log: ${isExpanded ? 'collapse' : 'expand'} row`
          );
        },
      }
    : null,
});
